<template>
  <div class="document-block">
    <div v-if="documents.length === 0 && preview" class="block-empty">
      <p>No documents to display</p>
    </div>

    <template v-else-if="documents.length > 0">
      <h2 v-if="collectionTitle" class="collection-title">{{ collectionTitle }}</h2>

      <div class="document-list">
        <div
          v-for="doc in documents"
          :key="doc.id"
          class="document-item"
          :class="{ expanded: isExpanded(doc.id) }"
        >
          <div class="document-header" @click="toggleDocument(doc.id)">
            <div class="document-info">
              <span class="document-title">{{ doc.title }}</span>
              <span v-if="doc.label" class="document-label">{{ doc.label }}</span>
            </div>
            <ChevronDownIcon class="doc-chevron" :class="{ open: isExpanded(doc.id) }" />
          </div>

          <div v-if="isExpanded(doc.id)" class="document-content">
            <p v-if="doc.description" class="document-description">{{ doc.description }}</p>

            <object
              v-if="pdfUrl(doc) && pdfStatus[doc.id] !== 'failed'"
              type="application/pdf"
              :data="pdfUrl(doc) + '#view=FitH'"
              width="100%"
              class="pdf-viewer"
            >
              <div class="pdf-probe" :data-doc-id="doc.id">&nbsp;</div>
            </object>

            <p v-if="pdfStatus[doc.id] === 'failed'" class="pdf-fallback-msg">
              Inline preview not available in this browser.
            </p>

            <a v-if="pdfUrl(doc)" :href="pdfUrl(doc)" class="document-download-link" target="_blank" rel="noopener">
              Download / Open PDF &rarr;
            </a>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import ChevronDownIcon from '@/assets/icons/chevron-down.vue'
import { wrapperToDirectUrl } from '@/utils/imageUrl'

export default {
  name: 'DocumentBlock',
  components: { ChevronDownIcon },
  props: {
    data: {
      type: Object,
      default: () => ({})
    },
    preview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      expandedDocId: null,
      pdfStatus: {}
    }
  },
  computed: {
    documents() {
      return this.data.documents || []
    },
    collectionTitle() {
      return this.data.title || ''
    }
  },
  watch: {
    expandedDocId(newId) {
      if (!newId || this.pdfStatus[newId]) return
      this.$nextTick(() => {
        setTimeout(() => {
          const probe = this.$el.querySelector(`.pdf-probe[data-doc-id="${newId}"]`)
          if (!probe) return
          const failed = probe.offsetHeight > 0
          this.pdfStatus = { ...this.pdfStatus, [newId]: failed ? 'failed' : 'ok' }
        }, 200)
      })
    }
  },
  methods: {
    pdfUrl(doc) {
      const url = wrapperToDirectUrl(doc.fileUrl) || doc.fileUrl
      return this.isValidHttpUrl(url) ? url : ''
    },
    toggleDocument(docId) {
      this.expandedDocId = this.expandedDocId === docId ? null : docId
    },
    isExpanded(docId) {
      return this.expandedDocId === docId
    }
  }
}
</script>

<style scoped>
.document-block {
  width: 100%;
  padding: 0;
}

.collection-title {
  font-family: var(--font-decorative);
  font-size: 24px;
  color: var(--color-text);
  margin: 0 0 20px 0;
}

.document-list {
  display: flex;
  flex-direction: column;
  border-top: 1px solid color-mix(in srgb, var(--color-text) 12%, var(--color-bg));
}

.document-item {
  border-bottom: 1px solid color-mix(in srgb, var(--color-text) 12%, var(--color-bg));
}

.document-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 4px;
  cursor: pointer;
  user-select: none;
}

.document-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.document-title {
  font-family: var(--font-decorative);
  font-size: 17px;
  color: var(--color-text);
}

.document-label {
  font-family: var(--font-primary);
  font-size: 12px;
  background: color-mix(in srgb, var(--color-text) 6%, var(--color-bg));
  padding: 2px 8px;
  border-radius: 3px;
  color: color-mix(in srgb, var(--color-text) 65%, var(--color-bg));
  white-space: nowrap;
}

.doc-chevron {
  flex-shrink: 0;
  margin-left: 12px;
  color: color-mix(in srgb, var(--color-text) 65%, var(--color-bg));
  transition: transform var(--transition-base);

  &.open {
    transform: rotate(180deg);
  }
}

.document-content {
  padding: 0 4px 16px;
}

.document-description {
  margin: 0 0 12px 0;
  font-family: var(--font-primary);
  font-size: var(--font-xs);
  line-height: var(--line-height-base);
  color: color-mix(in srgb, var(--color-text) 50%, var(--color-bg));
}

.pdf-viewer {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1.414;
  max-height: 600px;
  border: 1px solid color-mix(in srgb, var(--color-text) 12%, var(--color-bg));
  border-radius: var(--radius);
  margin-bottom: 10px;
}

.pdf-fallback-msg {
  margin: 0 0 10px;
  font-family: var(--font-primary);
  font-size: var(--font-sm);
  color: color-mix(in srgb, var(--color-text) 50%, var(--color-bg));
}

.document-download-link {
  font-family: var(--font-primary);
  font-size: 13px;
  color: var(--color-highlight);
  text-decoration: none;

  &:hover {
    color: var(--color-highlight-hover);
    text-decoration: underline;
  }
}

/* Mobile; admin previews always show the desktop page */
@media only screen and (max-width: 500px) {
  :where(.document-block:not(.preview-page *)) .document-title {
    font-size: 15px;
  }
}
</style>
